import { useEffect, useMemo, useRef, useState } from "react";
import { Download, Share2, X } from "lucide-react";
import { toast } from "@/lib/toast";
import { Button } from "./ui/button";
import { useAuth } from "../lib/auth";
import { capturePostHogEvent } from "../lib/posthog";

const DISMISS_KEY = "polaris_pwa_prompt_dismissed";
const VISIT_COUNT_KEY = "polaris_pwa_visit_count";
const FIRST_SEEN_KEY = "polaris_pwa_first_seen_at";
const MIN_VISITS = 3;
const MIN_AGE_MS = 1000 * 60 * 60 * 24;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
}

function isiOS() {
  if (typeof window === "undefined") return false;
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent);
}

function isSafari() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

export default function PwaInstallPrompt() {
  const { user } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "true");
  const [installed, setInstalled] = useState(() => isStandalone());
  const [installing, setInstalling] = useState(false);
  const [eligible, setEligible] = useState(false);
  const hasTrackedShown = useRef(false);

  useEffect(() => {
    const now = Date.now();
    const firstSeen = Number(localStorage.getItem(FIRST_SEEN_KEY) || 0);
    if (!firstSeen) {
      localStorage.setItem(FIRST_SEEN_KEY, String(now));
    }

    const nextVisitCount = Number(localStorage.getItem(VISIT_COUNT_KEY) || 0) + 1;
    localStorage.setItem(VISIT_COUNT_KEY, String(nextVisitCount));

    const ageMs = now - (firstSeen || now);
    const isLoggedIn = Boolean(user);
    setEligible(isLoggedIn && nextVisitCount >= MIN_VISITS && ageMs >= MIN_AGE_MS);
  }, [user]);

  useEffect(() => {
    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      capturePostHogEvent("pwa_install_available", {
        platform: "browser",
      });
    };

    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      localStorage.removeItem(DISMISS_KEY);
      capturePostHogEvent("pwa_installed", {
        platform: isiOS() ? "ios" : "browser",
      });
      toast.success("Polaris installed");
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const showIosHint = useMemo(() => !installed && !dismissed && !deferredPrompt && isiOS() && isSafari(), [installed, dismissed, deferredPrompt]);

  useEffect(() => {
    if (installed || dismissed || !eligible || (!deferredPrompt && !showIosHint) || hasTrackedShown.current) {
      return;
    }

    hasTrackedShown.current = true;
    capturePostHogEvent("pwa_install_prompt_shown", {
      platform: showIosHint ? "ios" : "browser",
      visit_count: Number(localStorage.getItem(VISIT_COUNT_KEY) || 0),
    });
  }, [dismissed, eligible, installed, deferredPrompt, showIosHint]);

  if (installed || dismissed || !eligible || (!deferredPrompt && !showIosHint)) {
    return null;
  }

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(DISMISS_KEY, "true");
    capturePostHogEvent("pwa_install_prompt_dismissed", {
      platform: showIosHint ? "ios" : "browser",
    });
  };

  const install = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    capturePostHogEvent("pwa_install_clicked", {
      platform: "browser",
    });
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        capturePostHogEvent("pwa_install_prompt_accepted", {
          platform: "browser",
        });
        toast.success("Installing Polaris");
      } else {
        capturePostHogEvent("pwa_install_prompt_rejected", {
          platform: "browser",
        });
      }
      setDeferredPrompt(null);
    } catch (error) {
      capturePostHogEvent("pwa_install_prompt_failed", {
        platform: "browser",
      });
      toast.error("Could not open install prompt");
    } finally {
      setInstalling(false);
    }
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 px-4 pb-4 mobile-safe-bottom">
      <div className="pointer-events-auto mx-auto w-full max-w-xl rounded-[28px] border border-[#E8DFD1] bg-white/95 p-4 shadow-[0_20px_60px_rgba(43,35,32,0.16)] backdrop-blur">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#2B2320] text-white">
            {showIosHint ? <Share2 className="w-5 h-5" /> : <Download className="w-5 h-5" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display text-lg tracking-tight text-[#2B2320]">Install Polaris</div>
                <p className="mt-1 text-sm leading-6 text-[#746A63]">
                  {showIosHint
                    ? "On iPhone or iPad, tap Share and choose Add to Home Screen for a faster app-style experience."
                    : "Add Polaris to your home screen for faster launch, fullscreen mode, and a more app-like workflow."}
                </p>
              </div>
              <button
                type="button"
                onClick={dismiss}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[#8B827A] transition hover:bg-[#F2EADB] hover:text-[#2B2320]"
                aria-label="Dismiss install prompt"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              {showIosHint ? (
                <Button type="button" variant="outline" className="rounded-full sm:w-auto" onClick={dismiss}>
                  Not now
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    onClick={install}
                    disabled={installing}
                    className="rounded-full bg-[#2B2320] hover:bg-[#201917] sm:w-auto"
                  >
                    <Download className="w-4 h-4 mr-1.5" />
                    {installing ? "Opening…" : "Install app"}
                  </Button>
                  <Button type="button" variant="outline" className="rounded-full sm:w-auto" onClick={dismiss}>
                    Not now
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
